import { useEffect, useState } from "react";
import { Box, TextField } from "@mui/material";
import { useShallow } from "zustand/react/shallow";

import styles from "../../../../assets/styles/componentStyles/templateLibraryStyles/whatsappTemplatePreview.module.scss";

import WrapWithHoverUI from "../utils/WrapWithHoverUI";
import VariableDialog from "../utils/VariableDialog";
import { renderVariableInText } from "../utils/renderVariableInText";
import { useTemplateLibraryStore } from "../../../../store/templateLibraryStore";
import { useCarouselFunctions } from "../utils/useCarouselFunctions";
import { handleKeyDownForFormatting, isTemplateCreated } from "../constant";

const CarouselTemplateBodyTextPreview = (props: any) => {

    const { editable = false, handleChangeText, displayTemplate } = props;

    const { createTemplatePayload } = useTemplateLibraryStore(useShallow((state) => ({
        createTemplatePayload: state.createTemplatePayload
    })));

    const { headerTextareaCarouselRef, handleFormattingCarousel, handleChangeCarouselVar, handleRemoveCarouselField, 
        handleCarouselMessageBodyTextChange } = useCarouselFunctions();

    const [editingText, setEditingText] = useState(false);
    const [variableDialogOpen, setVariableDialogOpen] = useState(false);
    const [variableDialogAnchorEl, setVariableDialogAnchorEl] = useState<HTMLElement | null>(null);
    const [activeVariableKey, setActiveVariableKey] = useState<string | null>(null);

    // Constants
    const carouselData = editable
        ? createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel
        : displayTemplate?.whatsapp_template_data?.whatsapp_template_carousel;

    const headerVariableData = carouselData?.whatsapp_template_carousel_header_dynamic_variable ?? [];
    const bodyTextVarsForDisplay = headerVariableData;
    const bodyTextData = editable
        ? (createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header ?? "")
        : (carouselData?.whatsapp_template_carousel_header ?? "");
    
    const isEmptyText = !bodyTextData?.trim();
    const hasMissingSampleText = bodyTextVarsForDisplay?.some(
        (variable: any) => !variable?.whatsapp_template_dynamic_variable_sample_value || variable?.whatsapp_template_dynamic_variable_sample_value?.trim() === ""
    );

    const isBodyTextDisabled = isTemplateCreated(createTemplatePayload);

    const bodyTextClassName = [
        styles.whatsappPreview__bodyText,
        styles["whatsappPreview__bodyText--carousel"],
        editable && (hasMissingSampleText || isEmptyText) ? styles.whatsappPreview__textInputError : "",
    ].filter(Boolean).join(" ");

    // Functions
    const handleVariableAreaClick = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        const variableElement = target.closest?.('[data-variable="true"]') || target.closest?.(`.${styles.whatsappPreview__variable}`);

        if (variableElement) {
            e.preventDefault();
            e.stopPropagation();
            setVariableDialogAnchorEl(variableElement as HTMLElement);
            setActiveVariableKey((variableElement as HTMLElement).getAttribute("data-variable-key"));
            setVariableDialogOpen(true);
        } else if (editable) {
            setEditingText(true);
        }
    };

    const handleVariableDialogClose = () => {
        setVariableDialogOpen(false);
        setVariableDialogAnchorEl(null);
        setActiveVariableKey(null);
    };

    const wrapWithHoverUI = (content: React.ReactNode) => (
        <WrapWithHoverUI
            label="Message Body Text"
            editable={editable}
            onHoverChange={() => {}}
            onMouseLeave={() => setEditingText(false)}
            containerClassName={styles.whatsappPreview__carouselBodyTextHover}
        >
            {content}
        </WrapWithHoverUI>
    );

    const renderBodyTextPreview = () => (
        <Box 
            className={bodyTextClassName}
            dir="auto"
        >
            {bodyTextData?.trim()
                ? renderVariableInText(bodyTextData, bodyTextVarsForDisplay)
                : (
                    <span
                        className={styles.whatsappPreview__textContentPreview}
                        dangerouslySetInnerHTML={{ __html: "<strong>Enter message body text</strong>" }}
                    />
                )}
        </Box>
    );

    useEffect(() => {
        if (!editable) {
            setVariableDialogOpen(false);
            setVariableDialogAnchorEl(null);
            setEditingText(false);
        }
    }, [editable]);

    useEffect(() => {
        if (editingText && headerTextareaCarouselRef?.current) {
            const textTimeout = setTimeout(() => {
                const textareaElement = headerTextareaCarouselRef?.current;
                if (!textareaElement) return;
                textareaElement.focus();
                const bodyValueLength = (createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header ?? "").length;
                textareaElement.setSelectionRange(bodyValueLength, bodyValueLength);
            }, 0);
            return () => clearTimeout(textTimeout);
        }
    }, [editingText, headerTextareaCarouselRef, createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header]);

    if (!editable) {
        return renderBodyTextPreview();
    }

    return (
        <>
            {wrapWithHoverUI(
                <div onClick={handleVariableAreaClick} className={styles.whatsappPreview__editableTextFieldWrapper}>
                    {editingText ? (
                        <TextField
                            multiline
                            fullWidth
                            InputProps={{ className: styles.whatsappPreview__textInputHoverContainer }}
                            inputProps={{ dir: 'auto' }}
                            disabled={isBodyTextDisabled}
                            inputRef={headerTextareaCarouselRef}
                            className={bodyTextClassName}
                            value={createTemplatePayload?.whatsapp_template_data?.whatsapp_template_carousel?.whatsapp_template_carousel_header ?? ""}
                            onChange={(event: any) => {handleCarouselMessageBodyTextChange(event, handleChangeText)}}
                            onKeyDown={(e) => handleKeyDownForFormatting(e, handleFormattingCarousel, "header")}
                            autoComplete="off"
                            minRows={1}
                            placeholder="Enter message body text"
                        />
                    ) : (
                        renderBodyTextPreview()
                    )}
                </div>
            )}

            {editable && (
                <VariableDialog
                    open={variableDialogOpen}
                    anchorEl={variableDialogAnchorEl}
                    onClose={handleVariableDialogClose}
                    title="Variable"
                    variableData={headerVariableData}
                    variableType="header"
                    activeVariableKey={activeVariableKey}
                    onChange={(key, event) => handleChangeCarouselVar(key, event, "header")}
                    onRemoveField={(key) => handleRemoveCarouselField(key, "header")}
                    hasMissingSampleText={hasMissingSampleText}
                    disabled={isBodyTextDisabled}
                />
            )}
        </>
    );
};

export default CarouselTemplateBodyTextPreview;